import moment from "moment";
import { INITIAL_TABLE_HEADERS, CALCULATED_KEYS } from "@/views/financialReports/config/reportStructure";
import { getResultStateByPeriod } from "@/api/financialReport";
import { resolveDateRange } from "./utils/dateRange";

const YEARS_SHOWED = 4
const state = {
    filterPeriod: 'month',
    tableRowHeaders: JSON.parse(JSON.stringify(INITIAL_TABLE_HEADERS)),
    netSellsDetail: {},
    maxCostsDetail: {},
    maxExpensesDetail: {},
    isLoading: false,
    error: null
};

const getters = {
    getHeadersTable: (state, _getters, rootState) => () => {
        const endMonth = rootState.reportsFilters.endMonth;
        const selectedPeriodRange = rootState.reportsFilters.selectedPeriodRange;
        let monthsToSubtract = 0;
        switch (selectedPeriodRange) {
            case 'lastYear': monthsToSubtract = 12; break;
            case 'last24months': monthsToSubtract = 24; break;
            case 'actualYear':
                monthsToSubtract = parseInt(moment(endMonth).format('M')) - 1;
                break;
            case 'previousYear':
                monthsToSubtract = 12;
                break;
        }

        const headers = [];
        const referenceDate = moment(endMonth);

        if (state.filterPeriod == 'year' || state.filterPeriod == 'ytd') {
            monthsToSubtract = 12 * YEARS_SHOWED
        }
        for (let i = monthsToSubtract; i >= 0; i--) {
            const date = moment(referenceDate).subtract(i, 'months');
            let label = '';
            switch (state.filterPeriod) {
                case 'month': label = date.format('MMM YYYY').replace('.', ''); break;
                case 'quarter': label = date.format('[Q]Q YYYY').replace('.', ''); break;
                case 'year': label = date.format('YYYY').replace('.', ''); break;
                case 'ytd': label = date.format('YYYY').replace('.', ''); break
            }
            if (!headers.includes(label)) {
                headers.push(label);
            }
        }
        return headers;
    }
};

const mutations = {
    SET_FILTER_PERIOD(state, period) {
        state.filterPeriod = period;
    },
    SET_REPORT_DATA(state, data) {
        state.tableRowHeaders = data.tableRowHeaders;
        state.netSellsDetail = data.netSellsDetail;
        state.maxCostsDetail = data.maxCostsDetail;
        state.maxExpensesDetail = data.maxExpensesDetail;
    },
    TOGGLE_CHILD_ROWS(state, isExpanded) {
        const childKeys = ['gastos_administracion', 'gastos_comerciales', 'gastos_i+d'];
        childKeys.forEach(key => {
            if (state.tableRowHeaders[key]) {
                state.tableRowHeaders[key].isShowed = isExpanded;
            }
        });
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    }
};

const actions = {
    async fetchReportData({ state, commit, getters, rootState }) {
        commit('SET_LOADING', true);
        try {
            // Backend handles company context via session/token
            let { initDate, endDate } = resolveDateRange(
                rootState.reportsFilters.endMonth,
                rootState.reportsFilters.selectedPeriodRange
            );

            if (state.filterPeriod == 'year' || state.filterPeriod == 'ytd') {
                initDate = moment(rootState.reportsFilters.endMonth)
                    .subtract(YEARS_SHOWED, 'year')
                    .startOf('year')
                    .format('YYYY-MM-DD');
            }

            const response = await getResultStateByPeriod(state.filterPeriod, initDate, endDate);

            const data = response.data.report_data;
            const allKeys = response.data.all_keys || [];

            const tableRowHeaders = JSON.parse(JSON.stringify(INITIAL_TABLE_HEADERS));

            // Process visibility and raw data
            Object.keys(tableRowHeaders).forEach(key => {
                // Set visibility based on backend's all_keys
                tableRowHeaders[key].isShowed = allKeys.includes(key);

                if (data && data[key]) {
                    tableRowHeaders[key].items = data[key];
                    tableRowHeaders[key].itemsDict = {};
                    data[key].forEach(item => {
                        let keyPeriod = moment(item.period).format(
                            state.filterPeriod === 'month' ? 'MMM YYYY' :
                                state.filterPeriod === 'quarter' ? '[Q]Q YYYY' : 'YYYY'
                        ).replace('.', '');
                        tableRowHeaders[key].itemsDict[keyPeriod] = Number(item.total_amount);
                    });
                } else {
                    tableRowHeaders[key].itemsDict = {};
                }
            });

            // Perform calculations based on the explicit CALCULATED_KEYS list
            const headers = getters.getHeadersTable();

            CALCULATED_KEYS.forEach(key => {
                const config = tableRowHeaders[key];
                if (!config) return;

                headers.forEach(header => {
                    const val = calculateKey(
                        tableRowHeaders,
                        config.keysSum || [],
                        config.keysSubtract || [],
                        config.keyDivide || '',
                        header
                    );

                    if (config.isMoney) {
                        tableRowHeaders[key].itemsDict[header] = val;
                    } else {
                        tableRowHeaders[key].itemsDict[header] = (val * 100).toFixed(2);
                    }
                });
            });

            commit('SET_REPORT_DATA', {
                tableRowHeaders,
                netSellsDetail: response.data.ventas_netas_detail,
                maxCostsDetail: response.data.max_costs_detail,
                maxExpensesDetail: response.data.max_expenses_detail
            });
            commit('TOGGLE_CHILD_ROWS', false)
        } catch (error) {
            console.error("Error fetching report data:", error);
            commit('SET_ERROR', error);
        } finally {
            commit('SET_LOADING', false);
        }
    }
};

function calculateKey(tableRowHeaders, keysSum, keysSubtract, keyDivide, columnHeader) {
    let sumAmount = 0;
    keysSum.forEach(key => {
        sumAmount += Number((tableRowHeaders[key] ?? {}).itemsDict?.[columnHeader] ?? 0);
    });

    let subtractAmount = 0;
    keysSubtract.forEach(key => {
        subtractAmount += Number((tableRowHeaders[key] ?? {}).itemsDict?.[columnHeader] ?? 0);
    });

    let divideAmount = 1;
    if (keyDivide !== '') {
        divideAmount = Number((tableRowHeaders[keyDivide] ?? {}).itemsDict?.[columnHeader] ?? 0);
    }

    if (divideAmount === 0) return 0;
    return (sumAmount - subtractAmount) / (keyDivide !== '' ? divideAmount : 1);
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
